import { defineComponent, h } from 'vue';
import { QCircularProgress } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';

export default defineComponent({
    name: 'cc-progress-circle',
    components: { QCircularProgress },
    mixins: [BaseComponent],
    data: function () {
        return {
            tooltipEval: null,
            tooltipPlacementEval: null,
            disabledEval: null,
            objectFitEval: null,
            color_fn: null,
            value_fn: null,
            width_fn: null,
            size_fn: null,
            rotate_fn: null,
            content_fn: null,
        };
    },
    computed: {
        style() {
            const hints = utils.resolveStyleHints(this.styleHints, this);
            const size0 = this.sizeStyle;
            return {
                ...hints,
                ...size0,
                overflow: 'hidden',
            };
        },
        tooltip() {
            try {
                if (this.tooltipEval === null && this.controlData.Tooltip)
                    this.tooltipEval = utils.compile(this, this.Translate(this.controlData.Tooltip));
                if (this.tooltipEval)
                    return utils.evaluate(this.tooltipEval, this);
                return '';
            }
            catch (e) {
                utils.warn('Tooltip could not evaluate expression: ' + this.controlData.Tooltip + '; ' + e);
                return '';
            }
        },
        tooltipPlacement() {
            try {
                if (this.tooltipPlacementEval === null && this.controlData.TooltipPlacement)
                    this.tooltipPlacementEval = utils.compile(this, this.controlData.TooltipPlacement);
                let placement = '';
                if (this.tooltipPlacementEval)
                    placement = utils.evaluate(this.tooltipPlacementEval, this);
                return placement.split('-')[0].toLowerCase() || 'top';
            }
            catch (e) {
                utils.warn('TooltipPlacement could not evaluate expression: ' + this.controlData.TooltipPlacement + '; ' + e);
                return '';
            }
        },
        color() {
            if (!this.color_fn && this.controlData.Color)
                this.color_fn = utils.compile(this, this.controlData.Color);
            return this.color_fn ? utils.evaluate(this.color_fn, this) : undefined;
        },
        rawValue() {
            if (!this.value_fn && this.controlData.ValueExpression)
                this.value_fn = utils.compileExpression(this, this.controlData.ValueExpression);
            return this.value_fn ? utils.evaluate(this.value_fn, this) : null;
        },
        hasValidValue() {
            return Number.isFinite(Number(this.rawValue));
        },
        value() {
            if (!this.hasValidValue)
                return 0;
            return Math.min(100, Math.max(0, Number(this.rawValue)));
        },
        width() {
            if (!this.width_fn && this.controlData.WidthExpression)
                this.width_fn = utils.compileExpression(this, this.controlData.WidthExpression);
            const w = this.width_fn ? utils.evaluate(this.width_fn, this) : 0.2;
            const n = Number(w);
            if (n >= 0 && n <= 1) return n;
            return 0.2;
        },
        size() {
            if (!this.size_fn && this.controlData.SizeExpression)
                this.size_fn = utils.compileExpression(this, this.controlData.SizeExpression);
            const s = this.size_fn ? utils.evaluate(this.size_fn, this) : 80;
            const n = Number(s);
            return (typeof n === 'number' && !isNaN(n)) ? `${n}px` : (typeof s === 'string' ? s : '80px');
        },
        rotate() {
            if (!this.rotate_fn && this.controlData.RotateExpression)
                this.rotate_fn = utils.compileExpression(this, this.controlData.RotateExpression);
            const r = this.rotate_fn ? utils.evaluate(this.rotate_fn, this) : 0;
            return Number(r) || 0;
        },
    },
    methods: {
        async preRenderComplete() {
            this.finishRenderHandler(this);
        },
        getContent(value) {
            if (!this.hasValidValue)
                return '-';
            if (!this.content_fn && this.controlData.Content)
                this.content_fn = utils.compile(this, this.controlData.Content, false, 'value');
            return this.content_fn ? utils.evaluate(this.content_fn, this, false, null, false, value) : null;
        },
    },
    render() {
        if (!this.todisplay)
            return null;

        try {
            const hasContent = this.controlData.Content;
            const contentSlot = hasContent
                ? { default: () => <span>{this.getContent(this.value)}</span> }
                : undefined;

            let item = (
                <div
                    v-show={this.isvisible}
                    style={this.style}
                    class={{ 'c-ProgressCircle': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                >
                    <QCircularProgress
                        value={this.value}
                        size={this.size}
                        thickness={this.width}
                        angle={this.rotate}
                        color={this.color}
                        indeterminate={this.controlData.Indeterminate}
                        showValue={!!hasContent}
                        {...(contentSlot ? { vSlots: contentSlot } : {})}
                    />
                </div>
            );

            if (this.tooltip)
                item = utils.generateTooltip(h, item, this.tooltip, this.tooltipPlacement);

            return item;
        }
        catch (e) {
            utils.error('ProgressCircle Render failed', e);
            return <div>ProgressCircle Failed to Render {e}</div>;
        }
    },
});
